import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Typography, Fab } from '@material-ui/core';
import { Add, Delete } from '@material-ui/icons';
import * as actions from "../../../BillingPlan/PlanVersions/store/planVersionsActions";
import * as action from '../../../../np-core/Auth/store/authActions';
import MaterialTable from 'material-table';
import { hasPermission, localization, hasAtLeastOnePermission } from '../../../../sc-utilities';
import PlanDelete from './PlansDelete';
import Toolbar from '../../../../sc-utilities/Toolbar'
import { MuiTableRows } from '../../../../../themes';
import PlansEdit from './PlansEdit';
import moment from 'moment';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const PlanList = props => {
    const { classes, translations, permissions, onAccountPlansList, fetching, saveColumnsFilter, activeCustomerID, tableColumns, extraFilterParams, onListPlans, billingAccount, assignPlans, removePlans, tableRef, onLoadAccount, onFetchPlanVersions } = props;

    const [deleteModalState, setDeleteModalState] = useState({ open: false, planIDs: [] });
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);
    const [plans, setPlans] = useState([]);
    const [modifyPlansState, setModifyPlansState] = useState({
        open: false,
        billingAccountID: extraFilterParams ? extraFilterParams.AccountID : null,
        loadingPlans: false
    });

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: hasAtLeastOnePermission(permissions, ["billing_account_plan_delete"]),
        search: false,
        minBodyHeight: window.innerHeight - 300 < 250 ? 250 : window.innerHeight - 300,
        maxBodyHeight: window.innerHeight - 300,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false,
    });

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }


    async function fetchTableData(query) {
        let q = filterQuery(query.filters);
        q['ValidTo'] = q['ValidTo'] ? moment(q['ValidTo']).set({ hour: 0, minute: 0, second: 0 }).format('YYYY-MM-DD HH:mm:ss') : null;
        query['ActiveAccountID'] = extraFilterParams.ActiveAccountID;
        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        let result = await onAccountPlansList(query);
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result?.data || [], page: query.page, pageSize: pageSize, totalCount: result?.data?.length || 0 };
    }

    const tableTitle = () => {
        return (
            <div className={classes.tableTitle}>
                <Typography className={classes.typography}>
                    {translations["account_plans"] || "Account plans"}
                </Typography>
                {hasPermission(permissions, "billing_account_plan_assign") ? (
                    <Fab
                        variant="extended"
                        size="small"
                        color="primary"
                        aria-label="Add"
                        style={{ borderRadius: '1px' }}
                        className={classes.fab}
                        onClick={handleAssign}
                    >
                        <Add size="small" />
                        {translations["add_plan"] || "Add plan"}
                    </Fab>
                ) : null}
            </div>
        )
    }

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, planIDs: [] });
    }

    const handleCloseDanger = async () => {
        await removePlans({ ActiveAccountID: extraFilterParams.ActiveAccountID, PlanIDs: deleteModalState.planIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, planIDs: [] });
        tableRef?.current && tableRef.current.onQueryChange()
    };

    const handleBulkDelete = planIDs => {
        setDeleteModalState({ ...deleteModalState, open: true, planIDs: planIDs });
    };

    // Table Settings
    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["account_plans"] || "Account plans"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(plan => plan.PlanID))
        }
    ]

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`Plan`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    }

    const handleAssign = async event => {
        if (!event) {
            setModifyPlansState({ ...modifyPlansState, open: false });
            return;
        }

        const billingAccountID = extraFilterParams ? extraFilterParams.ActiveAccountID : null;
        let q = {};
        q["UsedFor"] = billingAccount?.loaded?.BillingMode==="prepaid"? ["prepaid_only", "both"] : billingAccount?.loaded?.BillingMode==="postpaid"? ["postpaid_only", "both"] : null;
        let data = await onListPlans({ ActiveAccountID: billingAccountID, ActiveCustomerID: activeCustomerID, q: q });
        setPlans(data);
        setModifyPlansState({ ...modifyPlansState, billingAccountID: billingAccountID, open: true });
    };

    const handlePlansEdit = async (event, cancel) => {
        if (cancel) {
            setModifyPlansState({ ...modifyPlansState, open: false });
            return;
        }
        setModifyPlansState({ ...modifyPlansState, open: false });
        tableRef.current && tableRef.current.onQueryChange();
    }

    return (
        <>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {hasPermission(permissions, 'billing_account_plan_assign') ?
                <PlansEdit
                    onLoadAccount={onLoadAccount}
                    handlePlansEdit={handlePlansEdit}
                    billingAccount={{ ...billingAccount, AccountID: extraFilterParams.AccountID }}
                    modifyPlansState={modifyPlansState}
                    plans={plans}
                    open={modifyPlansState.open}
                    permissions={permissions}
                    translations={translations}
                    activeCustomerID={activeCustomerID}
                    assignPlans={assignPlans}
                    onFetchPlanVersions={onFetchPlanVersions}
                /> : null}
            {hasPermission(permissions, 'billing_account_plan_delete') ? (
                <PlanDelete
                    entityName={translations['account_plans'] || 'Account plans'}
                    translations={translations}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleteModalState={deleteModalState}
                />
            ) : null}
        </>
    );
};

const mapStateToProps = state => {
    return {
        fetching: state.billing.accounts.accountPlans.loading
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchPlanVersions: filter => dispatch(actions.fetchPlanVersions(filter)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(PlanList));